"use client";

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * Per-copy options.
 *
 * `success` is REQUIRED — a silent copy reads as broken, so every caller has to
 * decide. `false` is that decision written down: it means "this button already
 * confirms visually", not "no confirmation". Only pass it alongside
 * `confirmForMs`, which is what makes the statement true.
 */
interface CopyOptions {
  /**
   * Toast shown on success (localisable), or `false` for NO success toast.
   *
   * The three order dialogs (`PaymentLinkDialog`, `SendRewardDialog`,
   * `NewOrderClient`) swap their button to a check + "Copied" for 1800ms and
   * have never toasted. Adding one during their migration would have been a
   * user-visible change dressed up as a refactor.
   */
  success: string | false;
  /**
   * Toast shown when the clipboard is unavailable or the write rejects, or
   * `false` to stay silent (for a caller whose failure path is already visible).
   */
  error?: string | false;
  /**
   * Flip `copied` true for this many ms after a successful write.
   *
   * Deliberately OPT-IN. A labelled button that never reads `copied` would pay
   * for a pointless re-render plus a stray timer — a behaviour change with
   * nothing to show for it.
   */
  confirmForMs?: number;
  /**
   * Opt into the legacy off-screen-`<textarea>` + `document.execCommand("copy")`
   * path when the async Clipboard API is missing or rejects.
   *
   * ⚠ DEFAULT FALSE, and that default is load-bearing. `CopyButton`'s spec pins
   * that an ERROR TOAST fires when `navigator.clipboard` is undefined; silently
   * gaining a fallback would change what those callers do in a non-secure
   * context. Only `WeChatCopyButton` opts in — it shipped the fallback by hand,
   * and its visitors are the ones most likely to be in an in-app browser where
   * the async API is unavailable.
   */
  legacyFallback?: boolean;
}

const DEFAULT_ERROR = "Failed to copy";

/**
 * Copy `value` via the deprecated `execCommand` path.
 *
 * Returns whether the copy ACTUALLY happened. `execCommand` returns a boolean
 * and can also throw; treating either failure as success is how a "Copied!"
 * toast ends up lying to someone whose clipboard is still empty — which is
 * precisely the bug `WeChatCopyButton` shipped (it toasted success
 * unconditionally, after both paths had failed).
 */
function legacyCopy(value: string): boolean {
  if (typeof document === "undefined" || typeof document.execCommand !== "function") return false;
  const textArea = document.createElement("textarea");
  textArea.value = value;
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  } finally {
    document.body.removeChild(textArea);
  }
  return ok;
}

/**
 * The one clipboard-write path: async Clipboard API attempt, an optional legacy
 * fallback, success/error toast, and an optional short-lived `copied` flag.
 *
 * Falls back gracefully where the async clipboard API is unavailable (older
 * mobile browsers / non-secure contexts): without `legacyFallback` the write is
 * skipped and the caller's error toast tells the user to select the text
 * manually. The explicit `navigator.clipboard?.writeText` probe and the
 * try/catch are BOTH needed — the property can be missing, and the promise can
 * reject on a permission denial.
 *
 * The confirmation timer is cleared on unmount. Without that, a component
 * unmounted inside the confirm window (an order dialog closed on "Done", a
 * lightbox paged away) sets state after teardown — the defect
 * `ProductEnquireOptions` hand-rolled its own `copyTimerRef` to avoid.
 */
export function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    };
  }, []);

  async function copy(
    value: string,
    {
      success,
      error = DEFAULT_ERROR,
      confirmForMs,
      legacyFallback = false,
    }: CopyOptions
  ): Promise<void> {
    let ok = false;
    try {
      if (!navigator.clipboard?.writeText) throw new Error("no clipboard");
      await navigator.clipboard.writeText(value);
      ok = true;
    } catch {
      // Both failure shapes reach here — a missing API and a rejected write —
      // and the legacy path is the right answer to both.
      ok = legacyFallback ? legacyCopy(value) : false;
    }

    if (!ok) {
      if (error !== false) toast.error(error);
      return;
    }
    if (confirmForMs !== undefined) {
      setCopied(true);
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = setTimeout(() => setCopied(false), confirmForMs);
    }
    if (success !== false) toast.success(success);
  }

  return { copied, copy };
}
